import { useEffect, useMemo, useRef } from 'react';
import { SelectInput, Switch, TextInput } from '@my/ui';
import type { FerryUkCustomsProcedure, FerryVehicleType } from '@my/ferry-api';
import { ferryFreeBlocks } from '@my/ferry-api';
import { useFerryCapacity, useFerrySailings, useFerryVehicleTypes } from '@my/ferry-api/hooks';
import { bookingTypeShape } from '../../orders/lib/bookingForm';
import type { BookingFormValues } from '../../orders/lib/types';
import { formatPortTime, portDayBoundsUtc } from '../portTime';
import type { FerryCrossing } from '../useFerryCrossing';
import { defaultVehicleType } from '../vehicleType';

const UK_CUSTOMS_OPTIONS: Array<{ value: FerryUkCustomsProcedure | ''; label: string }> = [
  { value: '', label: '—' },
  { value: 'C', label: 'C' },
  { value: 'EX', label: 'EX' },
  { value: 'N', label: 'N' },
  { value: 'PL', label: 'PL' },
  { value: 'T1', label: 'T1' },
  { value: 'TX', label: 'TX' },
];

/** What the order knows about its load, for the defaults the old desktop took from the job. */
export interface FerryCargoHint {
  temperatureControlled: boolean;
  temperatureC: number | null;
  dangerousGoods: boolean;
  /** The cargo definition's equipment types, catalogue keys; the carrier's vehicle type follows the first one mapped. */
  equipmentKeys: string[];
}

const NO_KEYS: string[] = [];
const NO_TYPES: FerryVehicleType[] = [];

/**
 * The Sail time box on a Ferry-linked crossing. The operator types the time
 * as on any booking; the day's timetable from Ferry is shown underneath in
 * port time, with the organisation's free blocks on the matched sailing, and
 * a click on a departure fills the box. The typed time is matched against the
 * timetable here and refused at save when nothing sails then. When Ferry
 * holds no timetable for the day the time goes to the carrier as typed.
 */
export function FerrySailingSelect({
  organisationId,
  crossing,
  values,
  onChange,
  disabled,
}: {
  organisationId: string | null;
  crossing: FerryCrossing;
  values: BookingFormValues;
  onChange: (patch: Partial<BookingFormValues>) => void;
  disabled: boolean;
}) {
  const bounds = useMemo(
    () => (values.sailDate ? portDayBoundsUtc(values.sailDate, crossing.originZone) : null),
    [values.sailDate, crossing.originZone],
  );
  const sailingsQuery = useFerrySailings(
    crossing.carrierId,
    crossing.routeCode,
    bounds?.fromUtc ?? null,
    bounds?.toUtc ?? null,
  );
  const capacityQuery = useFerryCapacity(
    organisationId,
    crossing.carrierId,
    crossing.routeCode,
    bounds?.fromUtc ?? null,
    bounds?.toUtc ?? null,
  );

  const sailings = useMemo(
    () => (sailingsQuery.data ?? []).filter((s) => s.status !== 'Cancelled'),
    [sailingsQuery.data],
  );
  // The day's sailings by their port-time "HH:mm", which is what gets typed.
  const byTime = useMemo(
    () =>
      new Map(sailings.map((s) => [formatPortTime(s.scheduledDeparture, crossing.originZone), s])),
    [sailings, crossing.originZone],
  );
  const departures = useMemo(() => Array.from(byTime.keys()).sort().join(', '), [byTime]);

  const capacityByDeparture = useMemo(
    () => new Map((capacityQuery.data?.sailings ?? []).map((s) => [s.scheduledDeparture, s])),
    [capacityQuery.data],
  );
  const waitlistedByDeparture = useMemo(() => {
    const map = new Map<string, number>();
    for (const slot of capacityQuery.data?.slots ?? []) {
      if (slot.liveStatus !== 'Waitlisted') continue;
      map.set(slot.scheduledDeparture, (map.get(slot.scheduledDeparture) ?? 0) + 1);
    }
    return map;
  }, [capacityQuery.data]);

  // Capacity is only mirrored a few days ahead: no entry means "unknown", not zero.
  const capacityText = (departure: string): string => {
    const cap = capacityByDeparture.get(departure);
    if (!cap) return '';
    const standard = ferryFreeBlocks(cap, 'Standard');
    const high = ferryFreeBlocks(cap, 'High');
    const parts = [`${standard} block${standard === 1 ? '' : 's'} remaining`];
    if (high) parts.push(`${high} high`);
    const waitlisted = waitlistedByDeparture.get(departure) ?? 0;
    if (waitlisted) parts.push(`${waitlisted} on the waitlist`);
    return parts.join(' · ');
  };

  // Resolve the typed time against the timetable, and tell the form what the
  // day holds so the save can refuse a time nothing sails at.
  const typed = values.sailTime.slice(0, 5);
  useEffect(() => {
    if (!sailingsQuery.data) return;
    const match = typed ? (byTime.get(typed) ?? null) : null;
    const next = {
      ferrySailingUtc: match?.scheduledDeparture ?? '',
      ferryDayDepartures: departures,
    };
    if (
      next.ferrySailingUtc !== values.ferrySailingUtc ||
      next.ferryDayDepartures !== values.ferryDayDepartures
    ) {
      onChange(next);
    }
    // `onChange` is a fresh closure each render; the values it needs are listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sailingsQuery.data,
    byTime,
    departures,
    typed,
    values.ferrySailingUtc,
    values.ferryDayDepartures,
  ]);

  if (!values.sailDate) {
    return <TextInput label="Sail time" value="" readOnly placeholder="Pick the sail date first" />;
  }

  const matched = !!values.ferrySailingUtc;
  const selectedCapacity = matched ? capacityText(values.ferrySailingUtc) : '';

  return (
    <div>
      <TextInput
        label="Sail time *"
        type="time"
        value={values.sailTime}
        disabled={disabled}
        onChange={(e) => onChange({ sailTime: e.target.value })}
      />
      {sailingsQuery.isPending ? (
        <p className="mt-1 text-[11px] text-slate-500">
          Checking {crossing.carrierName}'s timetable…
        </p>
      ) : sailings.length === 0 ? (
        <p className="mt-1 text-[11px] text-slate-500">
          No timetable held for this day; the time is sent to {crossing.carrierName} as typed.
        </p>
      ) : (
        <p
          className={`mt-1 text-[11px] ${typed && !matched ? 'text-amber-700' : 'text-slate-500'}`}
        >
          {typed && !matched ? `No sailing at ${typed}. ` : ''}
          {crossing.carrierName} departures:{' '}
          {sailings.map((s) => {
            const at = formatPortTime(s.scheduledDeparture, crossing.originZone);
            return (
              <button
                key={s.id}
                type="button"
                disabled={disabled}
                title={s.shipName ?? undefined}
                onClick={() => onChange({ sailTime: at, ferrySailingUtc: s.scheduledDeparture })}
                className={`mr-1 underline ${
                  s.scheduledDeparture === values.ferrySailingUtc ? 'font-semibold' : ''
                }`}
              >
                {at}
              </button>
            );
          })}
          {selectedCapacity && `· ${selectedCapacity}`}
        </p>
      )}
    </div>
  );
}

/**
 * What the carrier needs beyond the TMS booking: which of the organisation's
 * accounts to book under (only when there are several), the cargo weight,
 * dangerous goods, and the customs references when the crossing changes
 * customs territory. The carrier's vehicle type is derived from the carrier
 * file and sent without being shown, for now.
 */
export function FerryBookingFields({
  crossing,
  values,
  onChange,
  disabled,
  cargo = null,
}: {
  crossing: FerryCrossing;
  values: BookingFormValues;
  onChange: (patch: Partial<BookingFormValues>) => void;
  disabled: boolean;
  /** The order's load, for the defaults; null when the surface has no order. */
  cargo?: FerryCargoHint | null;
}) {
  const vehicleTypesQuery = useFerryVehicleTypes(crossing.carrierId, true);

  // The carrier's types split by whether a driver travels; the TMS booking
  // type already says which (a Trailer booking is unaccompanied by definition).
  const accompanied = bookingTypeShape(values.bookingType).forceUnaccompanied
    ? false
    : values.isAccompanied;

  // The carrier's type for this booking, from the carrier file's mapping. The
  // keys are joined so a cargo object rebuilt each render does not re-derive.
  const reefer = !!cargo?.temperatureControlled;
  const equipmentKeyList = (cargo?.equipmentKeys ?? NO_KEYS).join('|');
  const derived = useMemo(
    () =>
      defaultVehicleType({
        map: crossing.vehicleTypes,
        equipmentKeys: equipmentKeyList ? equipmentKeyList.split('|') : NO_KEYS,
        bookingType: values.bookingType,
        accompanied,
        reefer,
        types: vehicleTypesQuery.data ?? NO_TYPES,
      }),
    [
      crossing.vehicleTypes,
      equipmentKeyList,
      values.bookingType,
      accompanied,
      reefer,
      vehicleTypesQuery.data,
    ],
  );

  // Fill the vehicle type whenever the form has none: on first show, and
  // again after a booking type or accompanied change clears it.
  useEffect(() => {
    if (values.ferryVehicleTypeCode || !derived.code) return;
    onChange({ ferryVehicleTypeCode: derived.code, ferryLengthMetres: derived.lengthMetres });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.ferryVehicleTypeCode, derived]);

  // Once per booking, from the order, as the old desktop took them from the
  // job: hazardous → dangerous goods; a reefer → one plug-in and its temperature.
  const prefilledFromCargo = useRef(false);
  useEffect(() => {
    if (prefilledFromCargo.current || !cargo) return;
    prefilledFromCargo.current = true;
    onChange({
      ferryDangerousGoods: cargo.dangerousGoods,
      ferryPlugIn: cargo.temperatureControlled,
      ferryPluginTemperature:
        cargo.temperatureControlled && cargo.temperatureC != null ? String(cargo.temperatureC) : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargo]);

  // Stena: PBN/GMR wherever the unit enters or leaves Britain's customs
  // territory, except Northern Ireland to Britain (unfettered access); the UK
  // customs procedure only from the Netherlands into Britain.
  const route = crossing.route;
  const from = route ? (route.originCustomsTerritory ?? route.originCountryIsoCode) : null;
  const to = route ? (route.destinationCustomsTerritory ?? route.destinationCountryIsoCode) : null;
  const needsCustomsIds = (from === 'GB') !== (to === 'GB') && !(from === 'XI' && to === 'GB');
  const needsCustomsProcedure = route?.originCountryIsoCode === 'NL' && to === 'GB';

  return (
    <>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {crossing.credentialRequired && (
          <SelectInput
            label={`${crossing.carrierName} account *`}
            name="booking-form-ferry-account"
            value={values.ferryCredentialId}
            options={[
              { value: '', label: 'Pick the account…' },
              ...crossing.credentials.map((c) => ({ value: c.id, label: c.customerCode })),
            ]}
            disabled={disabled}
            onChange={(e) => onChange({ ferryCredentialId: e.target.value })}
          />
        )}
        <Switch
          label="Dangerous goods"
          enabledLabel="Yes"
          disabledLabel="No"
          checked={values.ferryDangerousGoods}
          disabled={disabled}
          onChange={(e) => onChange({ ferryDangerousGoods: e.target.checked })}
        />
      </div>
      {(needsCustomsIds || needsCustomsProcedure) && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {needsCustomsProcedure && (
            <SelectInput
              label="UK customs procedure"
              name="booking-form-ferry-customs-procedure"
              value={values.ferryUkCustomsProcedure}
              options={UK_CUSTOMS_OPTIONS}
              disabled={disabled}
              onChange={(e) =>
                onChange({
                  ferryUkCustomsProcedure: e.target.value as FerryUkCustomsProcedure | '',
                })
              }
            />
          )}
          <TextInput
            label="Export reference (PBN)"
            name="booking-form-ferry-export-id"
            autoComplete="off"
            value={values.ferryExportId}
            disabled={disabled}
            maxLength={64}
            onChange={(e) => onChange({ ferryExportId: e.target.value.toUpperCase() })}
          />
          <TextInput
            label="Import reference (GMR)"
            name="booking-form-ferry-import-id"
            autoComplete="off"
            value={values.ferryImportId}
            disabled={disabled}
            maxLength={64}
            onChange={(e) => onChange({ ferryImportId: e.target.value.toUpperCase() })}
          />
        </div>
      )}
    </>
  );
}
